import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tag, Ticket, RefreshCw, Copy, Gift } from "lucide-react";

const LOOKUP_KEYS = [
  "verified_annual",
  "featured_listing_7d", "featured_listing_30d", "featured_listing_90d",
  "brief_boost_7d", "brief_boost_30d",
];

export default function AdminOffers() {
  const [tab, setTab] = useState("promo");         // "promo" | "discount" | "grant"
  const [promos, setPromos] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [promoForm, setPromoForm] = useState({
    code: "", percent_off: 10, amount_off: "", duration: "once",
    duration_in_months: 3, max_redemptions: "", expires_at: "",
    applies_to_lookup_keys: [],
  });
  const [discountForm, setDiscountForm] = useState({
    user_email: "", user_id: "", percent_off: 10, amount_off: "",
    duration: "once", duration_in_months: 3, name: "",
  });
  const [grantForm, setGrantForm] = useState({
    user_email: "", kind: "verified", target_id: "", days: 30, reason: "",
  });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/promo-codes").then(({ data }) => data.items || []).catch(() => []),
      api.get("/admin/discounts").then(({ data }) => data.items || []).catch(() => []),
      api.get("/admin/grants").then(({ data }) => data.items || []).catch(() => []),
    ]).then(([p, d, g]) => { setPromos(p); setDiscounts(d); setGrants(g); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleKey = (k) => {
    setPromoForm((f) => ({
      ...f,
      applies_to_lookup_keys: f.applies_to_lookup_keys.includes(k)
        ? f.applies_to_lookup_keys.filter((x) => x !== k)
        : [...f.applies_to_lookup_keys, k],
    }));
  };

  const createPromo = async (e) => {
    e.preventDefault();
    if (!promoForm.code) return toast.error("Code required");
    const usingPercent = !!promoForm.percent_off && !promoForm.amount_off;
    setCreating(true);
    try {
      const payload = {
        code: promoForm.code.toUpperCase(),
        duration: promoForm.duration,
        max_redemptions: promoForm.max_redemptions ? Number(promoForm.max_redemptions) : null,
        expires_at: promoForm.expires_at || null,
        applies_to_lookup_keys: promoForm.applies_to_lookup_keys.length ? promoForm.applies_to_lookup_keys : null,
      };
      if (usingPercent) payload.percent_off = Number(promoForm.percent_off);
      else payload.amount_off = Number(promoForm.amount_off);
      if (promoForm.duration === "repeating") payload.duration_in_months = Number(promoForm.duration_in_months);
      await api.post("/admin/promo-codes", payload);
      toast.success(`Promo code ${payload.code} created`);
      setPromoForm({ ...promoForm, code: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create promo");
    } finally { setCreating(false); }
  };

  const createDiscount = async (e) => {
    e.preventDefault();
    let user_id = discountForm.user_id;
    if (!user_id && discountForm.user_email) {
      try {
        const { data } = await api.get(`/admin/users?q=${encodeURIComponent(discountForm.user_email)}`);
        const found = (data.items || []).find((u) => u.email === discountForm.user_email.toLowerCase());
        if (!found) return toast.error("No user with that email");
        user_id = found.user_id;
      } catch { return toast.error("Lookup failed"); }
    }
    const usingPercent = !!discountForm.percent_off && !discountForm.amount_off;
    setCreating(true);
    try {
      const payload = {
        user_id: user_id || null,
        duration: discountForm.duration,
        name: discountForm.name || null,
      };
      if (usingPercent) payload.percent_off = Number(discountForm.percent_off);
      else payload.amount_off = Number(discountForm.amount_off);
      if (discountForm.duration === "repeating") payload.duration_in_months = Number(discountForm.duration_in_months);
      await api.post("/admin/discounts", payload);
      toast.success(user_id ? "Discount attached to customer" : "Coupon created");
      setDiscountForm({ ...discountForm, user_email: "", user_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create discount");
    } finally { setCreating(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(() => toast.success(`Copied ${code}`));
  };

  const createGrant = async (e) => {
    e.preventDefault();
    if (!grantForm.user_email) return toast.error("Customer email required");
    if (grantForm.kind !== "verified" && !grantForm.target_id) return toast.error("Target listing/brief ID required");
    setCreating(true);
    try {
      await api.post("/admin/grants", {
        user_email: grantForm.user_email,
        kind: grantForm.kind,
        target_id: grantForm.target_id || null,
        days: Number(grantForm.days),
        reason: grantForm.reason || null,
      });
      toast.success("Grant activated");
      setGrantForm({ ...grantForm, user_email: "", target_id: "", reason: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to grant");
    } finally { setCreating(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
      <div className="overline text-safety">Admin</div>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-display font-black text-4xl tracking-tighter text-forest">Offers & discounts</h1>
        <Button variant="ghost" size="sm" onClick={load} className="rounded-none" data-testid="offers-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-2 border-b border-border">
        <button onClick={() => setTab("promo")} data-testid="tab-promo" className={`px-4 py-2 overline text-xs ${tab === "promo" ? "border-b-2 border-forest text-forest" : "text-muted-foreground"}`}>
          <Ticket className="w-3.5 h-3.5 inline mr-1" /> Public promo codes
        </button>
        <button onClick={() => setTab("discount")} data-testid="tab-discount" className={`px-4 py-2 overline text-xs ${tab === "discount" ? "border-b-2 border-forest text-forest" : "text-muted-foreground"}`}>
          <Tag className="w-3.5 h-3.5 inline mr-1" /> Per-customer discounts
        </button>
        <button onClick={() => setTab("grant")} data-testid="tab-grant" className={`px-4 py-2 overline text-xs ${tab === "grant" ? "border-b-2 border-forest text-forest" : "text-muted-foreground"}`}>
          <Gift className="w-3.5 h-3.5 inline mr-1" /> Free grants
        </button>
      </div>

      {tab === "promo" && (
        <>
          <form onSubmit={createPromo} className="mt-6 border border-border bg-white p-6 space-y-4" data-testid="promo-form">
            <div className="font-display text-xl text-forest">New promo code</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Code *"><Input required value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} placeholder="LAUNCH50" data-testid="promo-code" className="rounded-none h-11 uppercase" /></Field>
              <Field label="% off"><Input type="number" min="1" max="100" value={promoForm.percent_off} onChange={(e) => setPromoForm({ ...promoForm, percent_off: e.target.value, amount_off: "" })} data-testid="promo-percent" className="rounded-none h-11" /></Field>
              <Field label="OR £ off"><Input type="number" step="0.01" min="0" value={promoForm.amount_off} onChange={(e) => setPromoForm({ ...promoForm, amount_off: e.target.value, percent_off: "" })} data-testid="promo-amount" className="rounded-none h-11" /></Field>
              <Field label="Duration">
                <select value={promoForm.duration} onChange={(e) => setPromoForm({ ...promoForm, duration: e.target.value })} data-testid="promo-duration" className="w-full h-11 rounded-none border border-border px-3">
                  <option value="once">Once (first invoice)</option>
                  <option value="repeating">Repeating (N months)</option>
                  <option value="forever">Forever</option>
                </select>
              </Field>
              {promoForm.duration === "repeating" && (
                <Field label="Repeating months"><Input type="number" min="1" max="24" value={promoForm.duration_in_months} onChange={(e) => setPromoForm({ ...promoForm, duration_in_months: e.target.value })} className="rounded-none h-11" /></Field>
              )}
              <Field label="Max redemptions"><Input type="number" min="1" value={promoForm.max_redemptions} onChange={(e) => setPromoForm({ ...promoForm, max_redemptions: e.target.value })} placeholder="unlimited" data-testid="promo-max" className="rounded-none h-11" /></Field>
            </div>

            <div>
              <div className="overline text-muted-foreground mb-2">Restrict to plans (optional)</div>
              <div className="flex flex-wrap gap-2">
                {LOOKUP_KEYS.map((k) => (
                  <button type="button" key={k} onClick={() => toggleKey(k)} data-testid={`promo-plan-${k}`} className={`text-xs overline px-3 py-1.5 border ${promoForm.applies_to_lookup_keys.includes(k) ? "bg-forest text-white border-forest" : "border-border text-muted-foreground"}`}>{k}</button>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-border flex justify-end">
              <Button type="submit" disabled={creating} data-testid="promo-create-btn" className="rounded-none bg-forest hover:bg-forest-800 text-white h-11 px-6">
                {creating ? "Creating…" : "Create promo code →"}
              </Button>
            </div>
          </form>

          <section className="mt-8">
            <div className="overline text-muted-foreground mb-3">Existing promo codes</div>
            {loading ? <div className="p-8 text-center text-muted-foreground">Loading…</div> :
             promos.length === 0 ? <div className="p-8 text-center text-muted-foreground border border-border">None yet.</div> :
              <div className="border border-border bg-white">
                {promos.map((p) => (
                  <div key={p.promotion_code_id} data-testid={`promo-row-${p.code}`} className="p-4 grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center border-t border-border first:border-t-0">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-mono text-lg font-bold text-forest">{p.code}</div>
                        <button onClick={() => copyCode(p.code)} className="text-muted-foreground hover:text-forest" data-testid={`promo-copy-${p.code}`}><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      {p.applies_to_lookup_keys?.length > 0 && (
                        <div className="mt-1 text-[10px] text-muted-foreground truncate">Applies to: {p.applies_to_lookup_keys.join(", ")}</div>
                      )}
                    </div>
                    <Badge className="rounded-none overline text-[9px] bg-forest text-white">{p.times_redeemed}/{p.max_redemptions ?? "∞"}</Badge>
                    <Badge className={`rounded-none overline text-[9px] ${p.active ? "bg-safety text-white" : "bg-muted text-forest"}`}>{p.active ? "active" : "inactive"}</Badge>
                    <div className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString("en-GB")}</div>
                  </div>
                ))}
              </div>
            }
          </section>
        </>
      )}
      {tab === "discount" && (
        <>
          <form onSubmit={createDiscount} className="mt-6 border border-border bg-white p-6 space-y-4" data-testid="discount-form">
            <div className="font-display text-xl text-forest">New per-customer discount</div>
            <p className="text-sm text-muted-foreground">Attach a coupon to a single customer&apos;s Stripe account — auto-applies to their next invoice.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Customer email"><Input type="email" value={discountForm.user_email} onChange={(e) => setDiscountForm({ ...discountForm, user_email: e.target.value })} placeholder="corp.buyer@example.com" data-testid="discount-email" className="rounded-none h-11" /></Field>
              <Field label="Coupon name (internal)"><Input value={discountForm.name} onChange={(e) => setDiscountForm({ ...discountForm, name: e.target.value })} placeholder="Q1 loyalty bonus" data-testid="discount-name" className="rounded-none h-11" /></Field>
              <Field label="% off"><Input type="number" min="1" max="100" value={discountForm.percent_off} onChange={(e) => setDiscountForm({ ...discountForm, percent_off: e.target.value, amount_off: "" })} data-testid="discount-percent" className="rounded-none h-11" /></Field>
              <Field label="OR £ off"><Input type="number" step="0.01" min="0" value={discountForm.amount_off} onChange={(e) => setDiscountForm({ ...discountForm, amount_off: e.target.value, percent_off: "" })} data-testid="discount-amount" className="rounded-none h-11" /></Field>
              <Field label="Duration">
                <select value={discountForm.duration} onChange={(e) => setDiscountForm({ ...discountForm, duration: e.target.value })} data-testid="discount-duration" className="w-full h-11 rounded-none border border-border px-3">
                  <option value="once">Once (next invoice)</option>
                  <option value="repeating">Repeating (N months)</option>
                  <option value="forever">Forever</option>
                </select>
              </Field>
              {discountForm.duration === "repeating" && (
                <Field label="Repeating months"><Input type="number" min="1" max="24" value={discountForm.duration_in_months} onChange={(e) => setDiscountForm({ ...discountForm, duration_in_months: e.target.value })} className="rounded-none h-11" /></Field>
              )}
            </div>
            <div className="pt-4 border-t border-border flex justify-end">
              <Button type="submit" disabled={creating} data-testid="discount-create-btn" className="rounded-none bg-forest hover:bg-forest-800 text-white h-11 px-6">
                {creating ? "Creating…" : "Attach discount →"}
              </Button>
            </div>
          </form>

          <section className="mt-8">
            <div className="overline text-muted-foreground mb-3">Attached discounts</div>
            {loading ? <div className="p-8 text-center text-muted-foreground">Loading…</div> :
             discounts.length === 0 ? <div className="p-8 text-center text-muted-foreground border border-border">None yet.</div> :
              <div className="border border-border bg-white">
                {discounts.map((d) => (
                  <div key={d.coupon_id} data-testid={`discount-row-${d.coupon_id}`} className="p-4 grid grid-cols-[1fr,auto,auto] gap-4 items-center border-t border-border first:border-t-0">
                    <div>
                      <div className="font-medium text-sm">{d.name}</div>
                      <div className="text-xs text-muted-foreground">{d.attached_to_customer ? `Customer: ${d.attached_to_customer}` : "Unattached coupon"}</div>
                    </div>
                    <Badge className="rounded-none bg-forest text-white overline text-[9px]">{d.percent_off ? `${d.percent_off}% off` : `£${d.amount_off} off`}</Badge>
                    <div className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString("en-GB")}</div>
                  </div>
                ))}
              </div>
            }
          </section>
        </>
      )}
      {tab === "grant" && (
        <>
          <form onSubmit={createGrant} className="mt-6 border border-border bg-white p-6 space-y-4" data-testid="grant-form">
            <div className="font-display text-xl text-forest">Grant a free perk</div>
            <p className="text-sm text-muted-foreground">
              Comp a Verified badge, featured listing, or featured clearance request at zero cost — no Stripe charge. Fully audited.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Customer email *">
                <Input required type="email" value={grantForm.user_email} onChange={(e) => setGrantForm({ ...grantForm, user_email: e.target.value })} placeholder="customer@company.co.uk" data-testid="grant-email" className="rounded-none h-11" />
              </Field>
              <Field label="Perk kind">
                <select value={grantForm.kind} onChange={(e) => setGrantForm({ ...grantForm, kind: e.target.value, target_id: "" })} data-testid="grant-kind" className="w-full h-11 rounded-none border border-border px-3">
                  <option value="verified">Verified badge</option>
                  <option value="listing_featured">Featured listing</option>
                  <option value="brief_featured">Featured clearance request</option>
                </select>
              </Field>
              {grantForm.kind !== "verified" && (
                <Field label={grantForm.kind === "listing_featured" ? "Listing ID *" : "Clearance request ID *"}>
                  <Input value={grantForm.target_id} onChange={(e) => setGrantForm({ ...grantForm, target_id: e.target.value })} placeholder={grantForm.kind === "listing_featured" ? "lst_…" : "brf_…"} data-testid="grant-target" className="rounded-none h-11 font-mono text-xs" />
                </Field>
              )}
              <Field label="Duration (days)">
                <Input type="number" min="1" max="3650" value={grantForm.days} onChange={(e) => setGrantForm({ ...grantForm, days: e.target.value })} data-testid="grant-days" className="rounded-none h-11" />
              </Field>
              <Field label="Reason (audit log)">
                <Input value={grantForm.reason} onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })} placeholder="Launch offer / apology comp / partnership" data-testid="grant-reason" className="rounded-none h-11" />
              </Field>
            </div>
            <div className="pt-4 border-t border-border flex justify-end">
              <Button type="submit" disabled={creating} data-testid="grant-create-btn" className="rounded-none bg-safety hover:bg-safety/90 text-white h-11 px-6">
                {creating ? "Granting…" : "Activate free perk →"}
              </Button>
            </div>
          </form>

          <section className="mt-8">
            <div className="overline text-muted-foreground mb-3">Recent grants</div>
            {loading ? <div className="p-8 text-center text-muted-foreground">Loading…</div> :
             grants.length === 0 ? <div className="p-8 text-center text-muted-foreground border border-border">None yet.</div> :
              <div className="border border-border bg-white">
                {grants.map((g) => (
                  <div key={g.grant_id} data-testid={`grant-row-${g.grant_id}`} className="p-4 grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center border-t border-border first:border-t-0">
                    <div>
                      <div className="font-medium text-sm">{g.user_email}</div>
                      <div className="text-xs text-muted-foreground">{g.reason || "—"}</div>
                    </div>
                    <Badge className="rounded-none bg-forest text-white overline text-[9px]">{g.kind}</Badge>
                    <Badge className="rounded-none bg-safety text-white overline text-[9px]">{g.days}d</Badge>
                    <div className="text-xs text-muted-foreground">{new Date(g.created_at).toLocaleDateString("en-GB")}</div>
                  </div>
                ))}
              </div>
            }
          </section>
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline text-muted-foreground mb-2">{label}</div>
      {children}
    </div>
  );
}
